import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../services/api';
import './SchoolSignup.css';

const LEARNER_COUNT_OPTIONS = ['0-50', '50-100', '100+'];

const PARTNER_CATEGORIES = [
  'immigration_office',
  'mobility_agency',
  'language_center',
  'training_center',
  'educational_agency',
  'student_service_office',
  'exam_prep_org'
];

const EducationPartnerSignup = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const [form, setForm] = useState({
    partnerCategory: '',
    organizationName: '',
    city: 'Algeria',
    contactName: '',
    phone: '',
    email: '',
    password: '',
    learnerCount: '',
    termsAccepted: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.partnerCategory) {
      setError(t('business.educationPartnerSignup.errorCategory'));
      return;
    }
    if (!form.organizationName?.trim()) {
      setError(t('business.educationPartnerSignup.errorOrganization'));
      return;
    }
    if (!form.contactName?.trim()) {
      setError(t('business.educationPartnerSignup.errorContact'));
      return;
    }
    if (!form.phone?.trim()) {
      setError(t('business.educationPartnerSignup.errorPhone'));
      return;
    }
    if (!form.email?.trim()) {
      setError(t('business.educationPartnerSignup.errorEmail'));
      return;
    }
    if (!form.password || form.password.length < 8) {
      setError(t('business.educationPartnerSignup.errorPassword'));
      return;
    }
    if (!form.termsAccepted) {
      setError(t('business.educationPartnerSignup.errorTerms'));
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      const email = form.email.trim().toLowerCase();
      const categoryLabel = t(`business.educationPartnerSignup.categories.${form.partnerCategory}`);
      const messageLines = [
        `[education_partner:${form.partnerCategory}]`,
        `Partner type: ${categoryLabel}`,
        `Contact: ${form.contactName.trim()}`,
        `City: ${(form.city || '').trim() || 'Algeria'}`,
        `Phone: ${form.phone.trim()}`
      ];

      formData.append('name', form.contactName.trim());
      formData.append('email', email);
      formData.append('schoolEmail', email);
      formData.append('schoolPassword', form.password);
      formData.append('partnerType', 'school');
      formData.append('schoolOrSocial', form.organizationName.trim());
      formData.append('audienceSize', form.learnerCount || '');
      formData.append('educationPartnerCategory', form.partnerCategory);
      formData.append('message', messageLines.join('\n'));
      formData.append('agreeTerms', 'true');

      const res = await fetch(`${getApiBaseUrl()}/api/partner-requests`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        setError(data.message || t('business.educationPartnerSignup.errorSubmit'));
        setLoading(false);
        return;
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || t('business.educationPartnerSignup.errorNetwork'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="school-signup-page education-partner">
      <nav className="school-signup-nav">
        <Link to="/business" className="back-link">
          <span className="material-icons">arrow_back</span> {t('business.educationPartnerSignup.back')}
        </Link>
        <Link to="/" className="logo-link">
          <span className="material-icons">school</span> Syllera
        </Link>
      </nav>

      <div className="school-signup-container">
        {submitted ? (
          <div className="school-signup-success">
            <span className="material-icons">mark_email_read</span>
            <h2>{t('business.educationPartnerSignup.successTitle')}</h2>
            <p>{t('business.educationPartnerSignup.successBody')}</p>
            <button type="button" className="school-signup-submit partner-btn" onClick={() => navigate('/business')}>
              {t('business.educationPartnerSignup.backToBusiness')}
            </button>
          </div>
        ) : (
          <>
            <h1 className="school-signup-title">{t('business.educationPartnerSignup.title')}</h1>
            <p className="school-signup-subtitle">{t('business.educationPartnerSignup.subtitle')}</p>

            <form onSubmit={handleSubmit} className="school-signup-form">
              {error && (
                <div className="school-signup-error">
                  <span className="material-icons">error_outline</span> {error}
                </div>
              )}

              <label className="school-signup-label" htmlFor="partnerCategory">
                {t('business.educationPartnerSignup.partnerType')} *
              </label>
              <select
                id="partnerCategory"
                name="partnerCategory"
                value={form.partnerCategory}
                onChange={handleChange}
                className="school-signup-input"
                required
              >
                <option value="">{t('business.educationPartnerSignup.selectType')}</option>
                {PARTNER_CATEGORIES.map((key) => (
                  <option key={key} value={key}>
                    {t(`business.educationPartnerSignup.categories.${key}`)}
                  </option>
                ))}
              </select>
              <p className="school-signup-hint">{t('business.educationPartnerSignup.typeHint')}</p>

              <label className="school-signup-label" htmlFor="organizationName">
                {t('business.educationPartnerSignup.organizationName')} *
              </label>
              <input
                id="organizationName"
                type="text"
                name="organizationName"
                value={form.organizationName}
                onChange={handleChange}
                placeholder={t('business.educationPartnerSignup.organizationPlaceholder')}
                className="school-signup-input"
                required
              />

              <label className="school-signup-label" htmlFor="city">
                {t('business.educationPartnerSignup.city')}
              </label>
              <input
                id="city"
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="Algeria"
                className="school-signup-input"
              />

              <label className="school-signup-label" htmlFor="contactName">
                {t('business.educationPartnerSignup.contactName')} *
              </label>
              <input
                id="contactName"
                type="text"
                name="contactName"
                value={form.contactName}
                onChange={handleChange}
                placeholder={t('business.educationPartnerSignup.contactPlaceholder')}
                className="school-signup-input"
                required
              />

              <label className="school-signup-label" htmlFor="phone">
                {t('business.educationPartnerSignup.phone')} *
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+213 ..."
                className="school-signup-input"
                required
              />

              <label className="school-signup-label" htmlFor="email">
                {t('business.educationPartnerSignup.email')} *
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="partner@example.com"
                className="school-signup-input"
                required
              />

              <label className="school-signup-label" htmlFor="password">
                {t('business.educationPartnerSignup.password')} *
              </label>
              <input
                id="password"
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="school-signup-input"
                minLength={8}
                required
              />

              <label className="school-signup-label" htmlFor="learnerCount">
                {t('business.educationPartnerSignup.learnerCount')}
              </label>
              <select
                id="learnerCount"
                name="learnerCount"
                value={form.learnerCount}
                onChange={handleChange}
                className="school-signup-input"
              >
                <option value="">{t('business.educationPartnerSignup.selectRange')}</option>
                {LEARNER_COUNT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <label className="school-signup-checkbox-label">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={form.termsAccepted}
                  onChange={handleChange}
                />
                <span>
                  {t('partnersApply.agreeToPartnerAgreement')}{' '}
                  <a href="/partners/partner-agreement" target="_blank" rel="noreferrer">
                    {t('partnersApply.partnerAgreement')}
                  </a>
                </span>
              </label>

              <button type="submit" className="school-signup-submit partner-btn" disabled={loading}>
                {loading
                  ? t('business.educationPartnerSignup.submitting')
                  : t('business.educationPartnerSignup.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default EducationPartnerSignup;
